// apps/dashboard/src/lib/api/class-management.ts
import { fetchApi } from './client';

// ── Tenant capability resolution ─────────────────────────────────────────────
// GET /v1/me/tenant-features unions the b2b capability flags across every
// partner membership the logged-in user has (see
// apps/api/src/modules/student/application/tenant-features.service.ts).
// Shape confirmed from that service's tests: `{ capabilities: string[] }`,
// always `{ capabilities: [] }` for a plain B2C user or on any resolution
// failure server-side (fail-closed at the source).
export interface TenantFeatures {
  capabilities: string[];
}

export const tenantFeaturesApi = {
  get: (): Promise<TenantFeatures> => fetchApi('/me/tenant-features'),
};

// ── Student's own classes ────────────────────────────────────────────────────
// GET /v1/class-management/students/me/classes — shape confirmed from
// apps/api/src/modules/class-management/domain/class-management.types.ts
// (StudentClassSummary) and the School/Class Prisma models in
// packages/database/prisma/schema/class.prisma.
export type SchoolStatus = 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED';
export type ClassStatus = 'DRAFT' | 'ACTIVE' | 'COMPLETED' | 'ARCHIVED';
export type ClassEnrollmentStatus = 'ACTIVE' | 'COMPLETED' | 'DROPPED';

export interface SchoolSummary {
  id: string;
  code: string;
  name: string;
  status: SchoolStatus;
}

export interface ClassSummary {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  startDate: string | null;
  endDate: string | null;
  termLabel: string | null;
  status: ClassStatus;
  school: SchoolSummary | null;
}

export interface StudentClassSummary {
  enrollmentId: string;
  enrollmentStatus: ClassEnrollmentStatus;
  enrolledAt: string;
  class: ClassSummary;
}

// ── Sessions + the student's own attendance ──────────────────────────────────
// GET /v1/class-management/students/me/classes/:classId/sessions — shape
// confirmed from student-classes.service.ts (`listClassSessions`) +
// StudentSessionRow in domain/class-management.types.ts. Scoped to the
// caller's own ACTIVE enrollment; 404 CLASS_NOT_FOUND when there isn't one.
export type AttendanceStatus = 'PRESENT' | 'LATE' | 'ABSENT' | 'EXCUSED';
export type CheckInMethod = 'TEACHER' | 'SELF';

export interface MyAttendance {
  status: AttendanceStatus;
  checkInMethod: CheckInMethod;
  markedAt: string;
}

export interface StudentSession {
  id: string;
  scheduledStart: string;
  scheduledEnd: string;
  myAttendance: MyAttendance | null;
  /** Computed SERVER-SIDE from the same SELF_CHECK_IN_GRACE_MINUTES window the
   * self-check-in endpoint enforces (15 min before scheduledStart → scheduledEnd).
   * The client must trust this rather than reimplement the rule — see
   * isSelfCheckInWindowOpen() in apps/api .../application/attendance.service.ts. */
  selfCheckInOpen: boolean;
}

// POST /v1/class-management/sessions/:id/attendance/self-check-in returns the
// created/updated Attendance row (always status PRESENT, method SELF).
export interface SelfCheckInResult {
  id: string;
  sessionId: string;
  enrollmentId: string;
  status: AttendanceStatus;
  checkInMethod: CheckInMethod;
  markedBy: string | null;
  markedAt: string;
}

export const classManagementApi = {
  listMyClasses: (): Promise<StudentClassSummary[]> =>
    fetchApi('/class-management/students/me/classes'),

  listClassSessions: (classId: string): Promise<{ sessions: StudentSession[] }> =>
    fetchApi(`/class-management/students/me/classes/${classId}/sessions`),

  selfCheckIn: (sessionId: string): Promise<SelfCheckInResult> =>
    fetchApi(`/class-management/sessions/${sessionId}/attendance/self-check-in`, {
      method: 'POST',
    }),
};
